import { Alert } from "@mui/material";

import { useSessionTopics } from "@/shared/domain/sessions/hooks";
import { type TopicIdentityRecord } from "@/shared/domain/topics";
import {
  buildColumnsForPreferences,
  colId,
  createColumnHelper,
  DataTable,
  deriveLoadingState,
  useDataTableState,
  type ColDef,
} from "@/shared/foundation/primitives/Table";
import { userFacingErrorMessage } from "@/shared/http";

import { useSessionOutletContext } from "./useSessionOutletContext";

const columnHelper = createColumnHelper<TopicIdentityRecord>();

const COLUMNS: ColDef<TopicIdentityRecord>[] = [
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 2,
  }),
  columnHelper.accessor("topic_id", {
    headerName: "Topic ID",
    flex: 1,
  }),
];

const DEFAULT_VISIBLE_IDS = COLUMNS.map(colId);

const PREFERENCE_DRIVEN_COLUMNS =
  buildColumnsForPreferences<TopicIdentityRecord>({
    staticColumns: COLUMNS,
    dynamicColumns: [],
    preferenceOrder: DEFAULT_VISIBLE_IDS,
  });

/**
 * Topics tab of the session-detail page. Lists the distinct topic
 * identities that contribute to this session, one page at a time, with
 * no filters or sort.
 */
export function TopicsTab() {
  const { session } = useSessionOutletContext();
  const query = useSessionTopics({ sessionId: session.session_id });

  const tableState = useDataTableState<TopicIdentityRecord>({
    getRowId: (row) => row.topic_id,
    defaultVisibleColumnIds: DEFAULT_VISIBLE_IDS,
  });

  if (query.isError) {
    return (
      <Alert severity="error">
        {userFacingErrorMessage(
          query.error,
          <>
            Failed to load topics. Please contact{" "}
            <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this
            problem persists.
          </>,
        )}
      </Alert>
    );
  }

  return (
    <DataTable.Root>
      <DataTable.Table<TopicIdentityRecord>
        {...tableState.table}
        columns={PREFERENCE_DRIVEN_COLUMNS}
        data={query.data?.items ?? []}
        hasNextPage={Boolean(query.data?.next_token)}
        loading={deriveLoadingState(query)}
        noRowsContent="No topics contribute to this session yet"
      />
    </DataTable.Root>
  );
}
